import { FILE_KINDS } from '@bower/shared/constants/fileKinds';
import type { FileKindId } from '@bower/shared';

import { ROUTES } from '../../constants/routes';

/**
 * Where the legacy workspace LIST pages' URLs land: the files list of the whole workspace (`scope=descendants`, list view),
 * narrowed to one kind where the legacy page was about one kind.
 *
 * Pure, so the mapping is testable without a router. Only `type` is carried
 * over; every other param belonged to the legacy page and is dropped.
 */

export interface LegacyLocation {
  readonly pathname: string;
  readonly search: string;
}

/**
 * `type=<artifactType>` to the kind that owns that artifact type, derived from
 * `FILE_KINDS` so a kind that claims a new artifact type is picked up here.
 * The legacy Files page keeps its Type filter in component state, not the URL,
 * so this serves typed links written by hand or bookmarked elsewhere.
 */
const KIND_BY_ARTIFACT_TYPE: ReadonlyMap<string, FileKindId> = new Map(
  FILE_KINDS.flatMap((kind) => (kind.artifactTypes ?? []).map((type) => [type, kind.id] as const)),
);

function workspaceList(kind: FileKindId | undefined): LegacyLocation {
  const params = new URLSearchParams({ scope: 'descendants' });
  if (kind) params.set('kinds', kind);
  params.set('view', 'list');
  return { pathname: ROUTES.collections, search: `?${params.toString()}` };
}

/** The new location for a legacy list page, or null when `pathname` is not one. */
export function mapLegacyWorkspaceLocation(location: LegacyLocation): LegacyLocation | null {
  // The router matches `/equipment/` to the `/equipment` route too.
  const pathname = location.pathname.replace(/\/+$/, '');
  if (pathname === ROUTES.equipment) return workspaceList('equipment');
  if (pathname !== ROUTES.artifacts) return null;
  const type = new URLSearchParams(location.search).get('type');
  return workspaceList(type ? KIND_BY_ARTIFACT_TYPE.get(type) : undefined);
}
